import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { Lora_700Bold } from '@expo-google-fonts/lora';
import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_600SemiBold,
} from '@expo-google-fonts/dm-sans';
import { useAuth } from '../../lib/contexts/AuthContext';
import { authService } from '../../lib/services/auth';
import { DynamicTitle } from '../DynamicTitle';

export function ComensalRecuperarContrasenaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email')?.trim() ?? '';

  const [fontsLoaded] = useFonts({
    Lora_700Bold,
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_600SemiBold,
  });
  const { profile, loading: authLoading } = useAuth();

  const [email, setEmail] = useState(emailParam);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [step, setStep] = useState<'send' | 'reset'>('send');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (emailParam) setEmail(emailParam);
  }, [emailParam]);

  useEffect(() => {
    if (authLoading || !profile) return;
    navigate('/', { replace: true });
  }, [authLoading, profile, navigate]);

  const showAlert = (title: string, msg: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.alert(`${title}\n\n${msg}`);
    } else {
      Alert.alert(title, msg);
    }
  };

  const handleEnviarCodigo = async () => {
    const e = email.trim();
    if (!e) {
      setFormError('Indica tu correo electrónico');
      return;
    }
    setFormError(null);
    setLoading(true);
    try {
      await authService.requestPasswordRecoveryOtp(e);
      setStep('reset');
      showAlert(
        'Código enviado',
        'Revisa tu correo e ingresa el código de verificación. Si no llega, revisa spam o espera unos segundos.'
      );
    } catch (err: any) {
      setFormError(err?.message ?? 'No se pudo enviar el código.');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarContrasena = async () => {
    const e = email.trim();
    if (!otp.trim()) {
      setFormError('Ingresa el código que recibiste por correo');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setFormError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('Las contraseñas no coinciden');
      return;
    }
    setFormError(null);
    setLoading(true);
    try {
      await authService.verifyOtpAndSetNewPassword(e, otp, newPassword);
      navigate('/', { replace: true });
    } catch (err: any) {
      setFormError(err?.message ?? 'Código incorrecto o sesión no válida. Solicita un código nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !fontsLoaded) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#A20000" />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <DynamicTitle title="Recuperar contraseña — As Gastronómico" />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.screenTitle}>Recuperar contraseña</Text>
          <Text style={styles.subtitle}>
            Te enviaremos un código a tu correo. Cuando lo recibas, vuelve aquí para ingresarlo y definir tu nueva
            contraseña.
          </Text>
        </View>

        {formError ? (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={20} color="#dc2626" style={styles.errorIcon} />
            <Text style={styles.errorText}>{formError}</Text>
          </View>
        ) : null}

        <View style={styles.form}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Correo electrónico</Text>
            <View style={styles.inputContainer}>
              <Ionicons name="mail-outline" size={20} color="#6b7280" style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                placeholder="tu@email.com"
                placeholderTextColor="#9ca3af"
                value={email}
                onChangeText={(t) => {
                  setEmail(t);
                  if (formError) setFormError(null);
                }}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                editable={!loading && step === 'send'}
              />
            </View>
          </View>

          {step === 'send' ? (
            <>
              <TouchableOpacity
                style={[styles.primaryButton, loading && styles.primaryDisabled]}
                onPress={handleEnviarCodigo}
                disabled={loading}
                activeOpacity={0.85}
              >
                {loading ? (
                  <ActivityIndicator color="#ffffff" />
                ) : (
                  <Text style={styles.primaryButtonText}>Enviar código</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.linkBelowPrimary}
                onPress={() => navigate('/entrar')}
                activeOpacity={0.7}
                disabled={loading}
              >
                <Text style={styles.linkBelowPrimaryText}>Volver a inicio de sesión</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Código del correo</Text>
                <View style={styles.inputContainer}>
                  <Ionicons name="keypad-outline" size={20} color="#6b7280" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder="123456"
                    placeholderTextColor="#9ca3af"
                    value={otp}
                    onChangeText={(t) => {
                      setOtp(t);
                      if (formError) setFormError(null);
                    }}
                    keyboardType="number-pad"
                    maxLength={12}
                    editable={!loading}
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Nueva contraseña</Text>
                <View style={styles.inputContainer}>
                  <Ionicons name="lock-closed-outline" size={20} color="#6b7280" style={styles.inputIcon} />
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    placeholder="Mínimo 6 caracteres"
                    placeholderTextColor="#9ca3af"
                    value={newPassword}
                    onChangeText={(t) => {
                      setNewPassword(t);
                      if (formError) setFormError(null);
                    }}
                    secureTextEntry={!showPass}
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!loading}
                  />
                  <TouchableOpacity style={styles.eyeIcon} onPress={() => setShowPass(!showPass)} disabled={loading}>
                    <Ionicons name={showPass ? 'eye-off-outline' : 'eye-outline'} size={20} color="#6b7280" />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Confirmar contraseña</Text>
                <View style={styles.inputContainer}>
                  <Ionicons name="lock-closed-outline" size={20} color="#6b7280" style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder="Repite la contraseña"
                    placeholderTextColor="#9ca3af"
                    value={confirmPassword}
                    onChangeText={(t) => {
                      setConfirmPassword(t);
                      if (formError) setFormError(null);
                    }}
                    secureTextEntry={!showPass}
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!loading}
                  />
                </View>
              </View>

              <TouchableOpacity
                style={[styles.primaryButton, loading && styles.primaryDisabled]}
                onPress={handleGuardarContrasena}
                disabled={loading}
                activeOpacity={0.85}
              >
                {loading ? (
                  <ActivityIndicator color="#ffffff" />
                ) : (
                  <Text style={styles.primaryButtonText}>Guardar contraseña</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() => {
                  setStep('send');
                  setOtp('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setFormError(null);
                }}
                disabled={loading}
              >
                <Text style={styles.secondaryBtnText}>Pedir otro código</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: '#ffffff' },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  scroll: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 24,
  },
  header: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    marginBottom: 24,
  },
  screenTitle: {
    fontFamily: 'Lora_700Bold',
    fontSize: 28,
    color: '#030213',
    marginBottom: 8,
    alignSelf: 'stretch',
  },
  subtitle: {
    fontFamily: 'DMSans_400Regular',
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
    alignSelf: 'stretch',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 8,
    padding: 14,
    marginBottom: 20,
  },
  errorIcon: { marginRight: 10, marginTop: 2 },
  errorText: {
    fontFamily: 'DMSans_500Medium',
    flex: 1,
    fontSize: 14,
    color: '#dc2626',
  },
  form: { width: '100%' },
  inputGroup: { marginBottom: 18 },
  label: {
    fontFamily: 'DMSans_600SemiBold',
    fontSize: 14,
    color: '#030213',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  inputIcon: { marginRight: 8 },
  input: {
    fontFamily: 'DMSans_400Regular',
    flex: 1,
    fontSize: 16,
    color: '#030213',
    paddingVertical: 12,
    minWidth: 0,
  },
  passwordInput: { paddingRight: 36 },
  eyeIcon: { padding: 4 },
  primaryButton: {
    backgroundColor: '#A20000',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  primaryDisabled: { opacity: 0.65 },
  primaryButtonText: {
    fontFamily: 'Lora_700Bold',
    color: '#ffffff',
    fontSize: 16,
  },
  linkBelowPrimary: {
    marginTop: 18,
    alignItems: 'center',
    paddingVertical: 8,
  },
  linkBelowPrimaryText: {
    fontFamily: 'Lora_700Bold',
    fontSize: 15,
    color: '#A20000',
    textDecorationLine: 'underline',
  },
  secondaryBtn: {
    marginTop: 16,
    alignSelf: 'center',
    paddingVertical: 8,
  },
  secondaryBtnText: {
    fontFamily: 'Lora_700Bold',
    fontSize: 14,
    color: '#A20000',
    textDecorationLine: 'underline',
  },
});
